import { z } from 'zod';

/**
 * 老板看板首屏五数（ADR-0047）。
 *
 * ## 契约层就把「0」和「算不出来」分开
 *
 * 这不是显示层的事——**类型不分开，显示层迟早会把 null 折成 0**：
 * 一句 `?? 0`、一个默认参数，就把「这个数算不出来」变成了「这个数是零」。
 * 而那两件事在看板上意味着完全相反的结论：
 *
 * - **「今天在运行 = 0」**：今天一次都没跑。**真值，有信息量**
 * - **「AI 替代率 = 0」**：不是 AI 一点没干，是**这个数没有源**。假值
 *
 * 故可算的三格是 `number`，无源的两格是 `{ value: null, blockedBy }`——
 * **拿不到数的时候必须同时拿到「为什么」**，否则界面只能显示「暂无数据」，
 * 而那句话会让人以为再等等就有了。
 *
 * 依据：P-13（不可判定不是「无」）、P-15（不建空视图占位——空视图会让人
 * 以为指标已可用，比缺失更坏）。
 */

/**
 * 一个此刻算不出来的指标。**没有 value 就必须有 blockedBy。**
 *
 * `.strict()` 不能省：zod 默认**剥掉**多余字段，不加它的话
 * `{ value: 0, blockedBy: '编制无源' }` 会解析通过——那是一个自相矛盾的对象
 * （既说「值是 0」又说「算不出来」），而它表达出来的效果是**替代率 0%**，
 * 会被读成「AI 完全没起作用」。
 *
 * 这是同一个坑第二次：昨天 `renameNodeTypeSchema` 少了 `.strict()`，
 * 注释里写着「多传的字段给了也不认」而实际被静默丢弃（ADR-0046 丙族第 11 例）。
 * **这一次是撞注释的那条用例先红的**——对策生效了。
 */
export const blockedNumberSchema = z
  .object({
    value: z.null(),
    /** 具体缺什么，不是「暂无数据」——后者不告诉人该去做什么。 */
    blockedBy: z.string().min(1),
  })
  .strict();

/** 可算的指标。**同样 `.strict()`**——带着 `blockedBy` 的数不许通过。 */
export const readyNumberSchema = z
  .object({
    value: z.number(),
  })
  .strict();

export const homeNumbersSchema = z.object({
  /** 在役岗位数。 */
  positions: z.number(),
  /**
   * 在岗人数。**统计值，不是填报值**——由人员表的在岗区间数出来
   * （`v_position_staffed_count`），没有任何界面能直接填它。
   *
   * 与岗位数同格显示：岗位数是主（大字），人数是辅（小字在下）。
   * **不新增第六格**——两个数回答的是同一个问题的两面：
   * 「有多少个岗位」与「这些岗位上现在有多少人」。
   */
  staffedPeople: z.number(),
  /**
   * 任务卡：**资产规模**，数卡不数执行（规范 4.1）。
   * 两态分开——`proposed` 还不是资产。
   */
  cards: z.object({
    modeled: z.number(),
    planned: z.number(),
  }),
  /** 今天在运行：**通量**。与上一项不可互相替代（ADR-0047 三）。 */
  runningToday: z.number(),

  /** AI 替代率。分母 = 岗位编制工时（MF-02）。 */
  aiSubstitutionRate: z.union([readyNumberSchema, blockedNumberSchema]),
  /** 日总工时中 AI 完成的部分。 */
  aiCompletedHours: z.union([readyNumberSchema, blockedNumberSchema]),
});

export type HomeNumbers = z.infer<typeof homeNumbersSchema>;
